import throttle from 'lodash/throttle'
import rewindListener from './rewindListener'
import { PlayerState } from 'components/UrPlayer/enums'

export type ProgressEvent =
  | 'player_5_percent'
  | 'player_10_percent'
  | 'player_25_percent'
  | 'player_50_percent'
  | 'player_75_percent'
  | 'player_85_percent'
  | 'player_90_percent'
  | 'player_95_percent'
  | 'player_100_percent'

export const progressEvent: Record<ProgressEvent, number> = {
  player_5_percent: 0.05,
  player_10_percent: 0.1,
  player_25_percent: 0.25,
  player_50_percent: 0.5,
  player_75_percent: 0.75,
  player_85_percent: 0.85,
  player_90_percent: 0.9,
  player_95_percent: 0.95,
  player_100_percent: 1.0,
}

let notTriggeredEvents: ProgressEvent[] = [
  'player_5_percent',
  'player_10_percent',
  'player_25_percent',
  'player_50_percent',
  'player_75_percent',
  'player_85_percent',
  'player_90_percent',
  'player_95_percent',
  'player_100_percent',
]

export const getTriggeredEvents = (
  progress: number,
  eventNames: ProgressEvent[],
): ProgressEvent[] => {
  const skipEventBecauseHandledSeparately = (e: string) =>
    e !== 'player_100_percent'

  return eventNames
    .filter(skipEventBecauseHandledSeparately)
    .reduce((acc: ProgressEvent[], eventName) => {
      if (progress >= progressEvent?.[eventName]) {
        return [...acc, eventName]
      }

      return acc
    }, [])
}

const removeTriggeredEvent = (triggeredEventName: ProgressEvent) => {
  notTriggeredEvents = notTriggeredEvents.filter(
    name => name !== triggeredEventName,
  )
}

export const getEventHandler = (
  onTimeEvent: (eventName: ProgressEvent) => void,
  getState: () => jwplayer.PlayState,
) => {
  return ({ position, duration }: { position: number; duration: number }) => {
    const progress = position / duration

    getTriggeredEvents(progress, notTriggeredEvents).forEach(triggeredEvent => {
      if (getState() === PlayerState.PLAYING) {
        onTimeEvent(triggeredEvent)
        removeTriggeredEvent(triggeredEvent)
      }
    })
  }
}

const timeEventListener = (
  jwPlayer: jwplayer.JWPlayer,
  onTimeEvent: (eventName: ProgressEvent) => void,
) => {
  const THROTTLE_TIME = 1000
  jwPlayer.on(
    'time',
    throttle(getEventHandler(onTimeEvent, jwPlayer.getState), THROTTLE_TIME, {
      leading: true,
    }),
  )

  jwPlayer.on('complete', () => {
    if (notTriggeredEvents.includes('player_100_percent')) {
      onTimeEvent('player_100_percent')
      removeTriggeredEvent('player_100_percent')
    }
  })

  rewindListener(jwPlayer, () => {
    notTriggeredEvents = Object.keys(progressEvent) as ProgressEvent[]
  })
}

export default timeEventListener
